import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Building2, Layers, Users, Headphones, Rocket, Landmark, Briefcase, Globe, Cpu, Truck, FlaskConical, Server } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = { Layers, Building2, Users, Headphones, Rocket, Landmark, Briefcase, Globe, Cpu, Truck, FlaskConical, Server };

// Small helper — renders <picture> with WebP fallback for local raster images.
// Kept at module scope so the visual-edits babel plugin can trace props cleanly
// (an IIFE returning conditional JSX was hitting deep-recursion in the plugin).
function IndustryImage({ img, title, isFirstRow }) {
  const isLocal = typeof img === "string" && img.startsWith("/") && /\.(png|jpe?g)$/i.test(img);
  const webp = isLocal ? img.replace(/\.(png|jpe?g)$/i, ".webp") : null;
  const alt = `${title} — modular office workstations and corporate office interior furniture by Ezeefit, manufacturer in Mumbai`;
  const cls = "w-full h-full object-cover group-hover:scale-105 transition-transform duration-500";
  const load = isFirstRow ? "eager" : "lazy";
  const fp = isFirstRow ? "high" : "low";
  if (webp) {
    return (
      <picture>
        <source srcSet={webp} type="image/webp" />
        <img src={img} alt={alt} className={cls} loading={load} decoding="async" fetchPriority={fp} />
      </picture>
    );
  }
  return <img src={img} alt={alt} className={cls} loading={load} decoding="async" fetchPriority={fp} />;
}

const coreIndustries = [
  { key: "it-technology", category: "IT & Technology", title: "Enterprise Tech Workspaces", subtitle: "High-density, modular office layouts with dedicated server room integration & agile collaboration pods.", icon: "Layers", image: "/industry/it-technology.jpg" },
  { key: "corporate-hq", category: "Corporate HQ Furniture", title: "Executive Office & Boardroom Solutions", subtitle: "Ergonomic executive desks, soundproof acoustic cabins, and premium boardroom furniture systems.", icon: "Building2", image: "/industry/corporate-hq.jpg" },
  { key: "coworking", category: "Co-working Space", title: "Flexible Co-Working & Shared Workspaces", subtitle: "Scalable hot-desking configurations, private dedicated cabins, and plug-and-play managed furniture infrastructure.", icon: "Users", image: "/industry/coworking.jpg" },
  { key: "bpo", category: "BPO & Call Center", title: "24/7 Operational Workstations", subtitle: "Acoustically treated, high-density linear workstations designed for round-the-clock intensive operations.", icon: "Headphones", image: "/industry/bpo.jpg" },
  { key: "startups", category: "Startup", title: "Scalable Startup Office Fit-Outs", subtitle: "Flexible, cost-effective office interior design built for rapidly expanding startup teams.", icon: "Rocket", image: "/industry/startups.jpg" },
  { key: "banking-finance", category: "Banking, Finance & Insurance", title: "BFSI Secure Workspaces", subtitle: "Compliant, high-security office layouts featuring biometric partitions, private cabins, and client lounge suites.", icon: "Briefcase", image: "/industry/banking-finance.jpg" },
  { key: "office-partitions", category: "Office Partitions & Cabin Systems", title: "Modular Glass Partitions & Cabin Systems", subtitle: "Acoustic double-glazed glass partitions, sliding wall dividers, and modular aluminum cabin frames.", icon: "Layers", image: "https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&w=1280&q=70" },
];

const emergingIndustries = [
  { key: "gcc", title: "Global Capability Centers (GCC)", subtitle: "Modern Workspaces for Global Operations", icon: "Globe" },
  { key: "fintech", title: "FinTech Company", subtitle: "Modern Spaces for Financial Innovation", icon: "Cpu" },
  { key: "logistics", title: "Logistics & Warehousing", subtitle: "Efficient Workspaces for Supply Chain", icon: "Truck" },
  { key: "pharma", title: "Pharma Corporate", subtitle: "Clean & Compliant Workspaces", icon: "FlaskConical" },
  { key: "data-centers", title: "Data Center", subtitle: "Mission-Critical Support Spaces", icon: "Server" },
];

export default function IndustrySolutions({ middleSlot = null }) {
  const [imageMap, setImageMap] = useState({});

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${API}/content/industrySolutions`);
        if (response.data?.items?.length > 0) {
          const map = {};
          response.data.items.forEach(item => {
            const slug = item.slug || item.id;
            if (slug && item.image) map[slug] = item.image;
          });
          setImageMap(map);
        }
      } catch (err) {
        console.error("Failed to fetch industry solutions:", err);
      }
    };
    fetchContent();
  }, []);

  return (
    <>
      {/* Hero Header */}
      <section className="py-24 bg-[#0F172A] noise-overlay" data-testid="industry-solutions-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <span className="font-mono text-sm text-[#F97316] tracking-wider uppercase mb-4 block">
              Industry-Specific Office Workspace Solutions
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Tailored Office & Workspace Solutions Designed for Your Industry
            </h2>
            <p className="text-lg text-[#94A3B8] max-w-2xl mx-auto">
              What workspace solution fits your sector? We deliver turn-key office solutions customized
              for every corporate office and upscaling need of your corporate office.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Core Industry Solutions */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h3 className="text-3xl font-bold text-[#0F172A]">Enterprise Workspace & Commercial Interior Solutions Across Industries</h3>
            <p className="text-[#64748B] mt-3 max-w-3xl mx-auto">Turn-key office layouts, custom modular workstations, and compliant corporate interior solutions engineered for high-growth sectors.</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {coreIndustries.map((item, index) => {
              const Icon = iconMap[item.icon] || Building2;
              const img = imageMap[item.key] || item.image;
              return (
                <motion.div
                  key={item.key}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                >
                  <Link
                    to="/products"
                    className="block group h-full"
                    data-testid={`home-solution-card-${item.key}`}
                  >
                    <div className="relative overflow-hidden h-full bg-[#F8FAFC] border border-[#E2E8F0] hover:border-[#F97316] transition-colors">
                      <div className="aspect-[4/3]">
                        <IndustryImage img={img} title={item.title} isFirstRow={index < 4} />
                      </div>
                      <div className="p-6">
                        <div className="w-12 h-12 bg-[#0F172A] flex items-center justify-center mb-4">
                          <Icon className="w-6 h-6 text-[#F97316]" />
                        </div>
                        <h4 className="text-lg font-bold text-[#0F172A] mb-1.5 group-hover:text-[#F97316] transition-colors line-clamp-2">
                          {item.category || item.title}
                        </h4>
                        <h5 className="text-sm font-semibold text-[#334155] mb-2 line-clamp-2">
                          {item.category ? item.title : ""}
                        </h5>
                        <p className="text-sm text-[#64748B] line-clamp-3">
                          {item.subtitle}
                        </p>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}

            {/* 8th slot — custom-industry CTA card (fills the 2×4 grid cleanly and funnels unmatched sectors to Contact) */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.35 }}
            >
              <Link
                to="/contact"
                className="block group h-full"
                data-testid="home-solution-card-custom-cta"
              >
                <div className="relative overflow-hidden h-full bg-[#0F172A] text-white border border-[#0F172A] hover:border-[#F97316] transition-colors flex flex-col justify-between">
                  <div className="aspect-[4/3] bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#B91C1C] flex items-center justify-center">
                    <ArrowRight className="w-14 h-14 text-[#F97316] group-hover:translate-x-2 transition-transform" strokeWidth={1.4} />
                  </div>
                  <div className="p-6">
                    <div className="w-12 h-12 bg-[#F97316] flex items-center justify-center mb-4">
                      <Briefcase className="w-6 h-6 text-white" />
                    </div>
                    <h4 className="text-lg font-bold mb-1.5 group-hover:text-[#F97316] transition-colors">
                      Your Industry Not Listed?
                    </h4>
                    <h5 className="text-sm font-semibold text-[#CBD5E1] mb-2">
                      Custom Corporate Workspace Solutions
                    </h5>
                    <p className="text-sm text-[#94A3B8]">
                      We build turn-key office interiors for every corporate sector. Share your brief and our team will respond within 24 hours.
                    </p>
                    <span className="inline-flex items-center gap-1.5 mt-4 text-[#F97316] text-sm font-semibold">
                      Request Consultation
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Optional slot rendered after Core industries (used on HomePage for client-logo marquee) */}
      {middleSlot}
    </>
  );
}
